import {
  TriangleAlertIcon,
  Avatar,
  Badge,
  Card,
  CheckIcon,
  XIcon,
  DropdownMenu,
  EllipsisVerticalIcon,
  Flex,
  Heading,
  IconButton,
  LinkIcon,
  LockIcon,
  PencilIcon,
  SendHorizontalIcon,
  Text,
  toast,
  Tooltip,
  TrashIcon,
} from '@buffer-mono/popcorn'

import type { PendingInvitation, TeamMember } from '../../hooks'
import { getDisplayName } from '../../utils/getDisplayName'

import styles from './TeamMemberRow.module.css'

import type { JSX } from 'react'

interface TeamMemberRowProps {
  teamMember: TeamMember
  onEdit: (member: TeamMember) => void
  onRemove: (member: TeamMember) => void
  canManage: boolean
  isCurrentUser?: boolean
}

function formatRelative(date: Date, now = new Date()): string {
  const diff = date.getTime() - now.getTime()
  const absDiffSec = Math.abs(diff) / 1000
  const day = 24 * 60 * 60
  if (absDiffSec < 60) return diff < 0 ? 'just now' : 'in less than a minute'
  if (absDiffSec < 60 * 60) {
    const mins = Math.round(absDiffSec / 60)
    return diff < 0
      ? `${mins} minute${mins === 1 ? '' : 's'} ago`
      : `in ${mins} minute${mins === 1 ? '' : 's'}`
  }
  if (absDiffSec < day) {
    const hrs = Math.round(absDiffSec / 3600)
    return diff < 0
      ? `${hrs} hour${hrs === 1 ? '' : 's'} ago`
      : `in ${hrs} hour${hrs === 1 ? '' : 's'}`
  }
  const days = Math.round(absDiffSec / day)
  return diff < 0
    ? `${days} day${days === 1 ? '' : 's'} ago`
    : `in ${days} day${days === 1 ? '' : 's'}`
}

export const TeamMemberRow = ({
  teamMember,
  onEdit,
  onRemove,
  canManage,
  isCurrentUser,
}: TeamMemberRowProps): JSX.Element => {
  // Count the distinct channels the member can access on *any* product, not
  // just Publish. After the per-product permissions split, a comments-only
  // member legitimately has Community access to channels with `publish: 'none'`
  // — counting only Publish would show them as "0 channels" despite real
  // access (CORE-6014).
  //
  // Iterating over `Object.values(productAccessLevels)` keeps this
  // product-agnostic: a channel counts when *any* product is granted, so future
  // products (e.g. Insights) are included without touching this code. Deduping
  // by channel id ensures a channel granted across multiple products is counted
  // once, not once per product.
  const accessibleChannelIds = new Set<string>()
  for (const {
    channel,
    productAccessLevels,
  } of teamMember.channelPermissions) {
    if (!channel) continue
    const hasAnyAccess = Object.values(productAccessLevels).some(
      (level) => level !== 'none',
    )
    if (hasAnyAccess) accessibleChannelIds.add(channel.id)
  }
  const channelCount = accessibleChannelIds.size

  const cardClassName = teamMember.isLocked
    ? `${styles.memberRow} ${styles.memberRowLocked}`
    : styles.memberRow

  const displayName = getDisplayName(teamMember)

  return (
    <Card className={cardClassName}>
      <Avatar
        src={teamMember.account?.avatar}
        alt={displayName}
        size="medium"
        className={styles.memberAvatar}
      />
      <Flex direction="column" gap="sm" className={styles.memberInfo}>
        <Flex
          align="start"
          direction="column"
          gap="xs"
          className={styles.memberHeader}
        >
          <Heading size="xs" as="p">
            {displayName}
          </Heading>
          <Flex align="center" gap="2xs">
            {teamMember.isOwner && (
              <Badge size="small" color="brand">
                Owner
              </Badge>
            )}
            {teamMember.organizationRole === 'admin' && !teamMember.isOwner && (
              <Badge size="small" color="subtle">
                Admin
              </Badge>
            )}
            {isCurrentUser && (
              <Badge size="small" color="neutral">
                You
              </Badge>
            )}
            {teamMember.isLocked && (
              <Tooltip content="This member's access was suspended due to a plan change. Remove or re-invite after upgrading.">
                <Badge size="small" color="critical">
                  <LockIcon size="xsmall" />
                  Locked
                </Badge>
              </Tooltip>
            )}
            <Tooltip
              content={
                teamMember.account?.tfa
                  ? 'Two-factor authentication enabled'
                  : 'Two-factor authentication not enabled'
              }
            >
              <Badge
                size="small"
                variant={teamMember.account?.tfa ? 'brand' : 'warning'}
              >
                {teamMember.account?.tfa ? (
                  <>
                    <CheckIcon />
                    2FA
                  </>
                ) : (
                  <>
                    <TriangleAlertIcon />
                    Pending 2FA
                  </>
                )}
              </Badge>
            </Tooltip>
          </Flex>
        </Flex>
        <Flex
          align="start"
          gap="2xs"
          direction="column"
          className={styles.memberDetails}
        >
          <Text color="subtle" className={styles.memberEmail}>
            {teamMember.email}
          </Text>
          <Text
            color="subtle"
            className={styles.memberDetailsSeparator}
            aria-hidden="true"
          >
            •
          </Text>
          <Text color="subtle">
            {teamMember.isLocked
              ? 'Access suspended'
              : `${channelCount} channel${channelCount !== 1 ? 's' : ''}`}
          </Text>
        </Flex>
      </Flex>

      {canManage && !teamMember.isOwner && (
        <Flex
          gap="2xs"
          className={styles.memberActions}
          direction="column"
          justify="center"
        >
          <Tooltip
            content={
              teamMember.isLocked
                ? 'Cannot edit locked member'
                : 'Edit team member'
            }
          >
            <IconButton
              label={`Edit ${displayName}`}
              variant="tertiary"
              onClick={(): void => onEdit(teamMember)}
              disabled={teamMember.isLocked}
            >
              <PencilIcon />
            </IconButton>
          </Tooltip>
          <Tooltip content="Remove team member">
            <IconButton
              label={`Remove ${displayName}`}
              variant="critical-subtle"
              onClick={(): void => onRemove(teamMember)}
            >
              <TrashIcon />
            </IconButton>
          </Tooltip>
        </Flex>
      )}
    </Card>
  )
}

interface PendingInvitationRowProps {
  invitation: PendingInvitation
  onRevoke?: (invitation: PendingInvitation) => void
  onResend?: (invitation: PendingInvitation) => void
  canManage: boolean
}

/**
 * Pending-invitation row: distinct visual treatment, no channel/role
 * editing, Revoke/Resend instead of Edit/Remove. Sourced from the
 * `pendingInvitations` query (an `Invitation`), not a `TeamMember`.
 */
export const PendingInvitationRow = ({
  invitation,
  onRevoke,
  onResend,
  canManage,
}: PendingInvitationRowProps): JSX.Element => {
  const displayName = invitation.name || invitation.email
  const invitedAt = new Date(invitation.invitedAt)
  const expiresAt = new Date(invitation.expiresAt)

  return (
    <Card className={styles.memberRow}>
      <Avatar alt={displayName} size="medium" className={styles.memberAvatar} />
      <Flex direction="column" gap="sm" className={styles.memberInfo}>
        <Flex
          align="start"
          direction="column"
          gap="xs"
          className={styles.memberHeader}
        >
          <Heading size="xs" as="p">
            {displayName}
          </Heading>
          <Flex align="center" gap="2xs">
            <Tooltip
              content={`Invited ${formatRelative(invitedAt)} · expires ${formatRelative(expiresAt)}`}
            >
              <Badge size="small" variant="warning">
                Pending
              </Badge>
            </Tooltip>
            {invitation.role === 'admin' && (
              <Badge size="small" color="subtle">
                Admin
              </Badge>
            )}
          </Flex>
        </Flex>
        <Flex
          align="start"
          gap="2xs"
          direction="column"
          className={styles.memberDetails}
        >
          <Text color="subtle" className={styles.memberEmail}>
            {invitation.email}
          </Text>
        </Flex>
      </Flex>

      {canManage && (
        <Flex
          gap="2xs"
          className={styles.memberActions}
          direction="column"
          justify="center"
        >
          <DropdownMenu>
            <DropdownMenu.Trigger>
              <IconButton label="Invitation options" variant="tertiary">
                <EllipsisVerticalIcon />
              </IconButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="end">
              <DropdownMenu.Item
                onSelect={(): void => {
                  navigator.clipboard
                    .writeText(invitation.inviteLink)
                    .then(() =>
                      toast.success('Invite link copied to clipboard'),
                    )
                    .catch(() => toast.error('Failed to copy invite link'))
                }}
              >
                <LinkIcon />
                Copy invite link
              </DropdownMenu.Item>
              {onResend && (
                <DropdownMenu.Item onSelect={(): void => onResend(invitation)}>
                  <SendHorizontalIcon />
                  Resend invitation
                </DropdownMenu.Item>
              )}
              {onRevoke && (
                <DropdownMenu.Item
                  variant="critical"
                  onSelect={(): void => onRevoke(invitation)}
                >
                  <XIcon />
                  Revoke invitation
                </DropdownMenu.Item>
              )}
            </DropdownMenu.Content>
          </DropdownMenu>
        </Flex>
      )}
    </Card>
  )
}
